import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useErrorToast } from "@/hooks/use-error-toast";
import { getInvoiceLabel } from "@/lib/invoiceStatus";
import { getProcessingStatusColor } from "@/lib/semanticColors";
import { pluralizeInvoices } from "@/lib/taskQueue";
import {
  useGetProcessingStatusesQuery,
  useUpdateInvoiceStatusMutation,
} from "@/services/invoices/invoicesApi";

/**
 * Moves an invoice to a different processing status. Reached by clicking the
 * status badge in any queue, the same affordance the old app had.
 *
 * The status list comes from the server rather than the local enum, because the
 * set of statuses an operator may pick is the server's to decide.
 */
/** The status radio list, shared by the single-row and bulk dialogs. */
function ProcessingStatusChoices({ statuses, isLoading, selected, onSelect, name = "processing-status" }) {
  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading statuses…
      </p>
    );
  }

  return (
    <fieldset className="invoice-choice-list">
      <legend className="sr-only">Processing status</legend>
      {statuses.map((status) => (
        <label key={status.id} className="invoice-choice">
          <input
            type="radio"
            name={name}
            value={status.id}
            checked={selected === status.id}
            onChange={() => onSelect(status.id)}
          />
          <span
            className="invoice-choice-mark"
            style={{ backgroundColor: getProcessingStatusColor(status.id) }}
            aria-hidden="true"
          />
          <span className="text-sm text-foreground">{status.name}</span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The same picker for a whole selection. It owns no mutation: there is no bulk
 * status endpoint, so the page runs one request per invoice through the queue
 * and reports the tally.
 */
export function InvoiceBulkStatusModal({ open, onOpenChange, count, onSubmit, isSubmitting }) {
  const { data: statuses = [], isLoading } = useGetProcessingStatusesQuery(undefined, {
    skip: !open,
  });
  const [selected, setSelected] = useState(null);
  const [wasOpen, setWasOpen] = useState(open);

  // Reset on open rather than in an effect, which would render the previous
  // choice once before clearing it.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSelected(null);
  }

  const submit = (event) => {
    event.preventDefault();
    if (selected == null) return;
    onSubmit(selected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Change status</DialogTitle>
            <DialogDescription>
              Applies to {pluralizeInvoices(count)}. Each is updated individually, so some may
              succeed while others fail.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <ProcessingStatusChoices
              statuses={statuses}
              isLoading={isLoading}
              selected={selected}
              onSelect={setSelected}
              name="bulk-processing-status"
            />
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[0.8rem] border-line shadow-none sm:min-w-24"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="h-9 rounded-[0.8rem] sm:min-w-24"
              disabled={isSubmitting || selected == null}
            >
              {isSubmitting ? (
                <>
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                  Updating
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceStatusModal({ invoice, open, onOpenChange }) {
  const showErrorToast = useErrorToast();
  const { data: statuses = [], isLoading } = useGetProcessingStatusesQuery(undefined, {
    skip: !open,
  });
  const [updateInvoiceStatus, { isLoading: isSaving }] = useUpdateInvoiceStatusMutation();

  const [selected, setSelected] = useState(null);
  const [seededGuid, setSeededGuid] = useState(null);

  // Seed from the invoice being edited during render, so the current status is
  // never briefly unselected when the dialog opens. Closing forgets the seed, so
  // reopening the same invoice starts from its status as it is now — not from a
  // choice cancelled earlier, or a status that has since changed another way.
  if (!open && seededGuid !== null) {
    setSeededGuid(null);
  } else if (open && invoice && invoice.id !== seededGuid) {
    setSeededGuid(invoice.id);
    setSelected(invoice.processingStatusId ?? null);
  }

  const submit = async (event) => {
    event.preventDefault();
    if (selected == null) return;

    try {
      await updateInvoiceStatus({ guid: invoice.id, processingStatusId: selected }).unwrap();
      toast.success("Status updated");
      onOpenChange(false);
    } catch (error) {
      showErrorToast(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Change processing status</DialogTitle>
            <DialogDescription>
              {invoice
                ? `${getInvoiceLabel(invoice)} · currently ${invoice.processingStatus ?? "unknown"}`
                : null}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <ProcessingStatusChoices
              statuses={statuses}
              isLoading={isLoading}
              selected={selected}
              onSelect={setSelected}
            />
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[0.8rem] border-line shadow-none sm:min-w-24"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="h-9 rounded-[0.8rem] sm:min-w-24"
              disabled={isSaving || selected == null || selected === invoice?.processingStatusId}
            >
              Update
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
